<script lang="ts">
  import { settingsStore, settingsActions, fontSizeMap, updateFontSizeVariables, updateFontFamilyVariables } from '$lib/stores/settings'
  import { onMount } from 'svelte'
  import { ChevronDown } from 'lucide-svelte'

  let theme = $state<'light' | 'dark' | 'auto'>('auto')
  let fontSize = $state<'small' | 'medium' | 'large'>('medium')
  let fontFamily = $state<'system-default' | 'noto-sans-kr' | 'noto-serif-kr' | 'gowun-batang' | 'gowun-dodum' | 'nanum-gothic' | 'nanum-myeongjo' | 'ibm-plex-sans-kr' | 'georgia' | 'sf-mono' | 'consolas' | 'monaco'>('system-default')
  let saveTimeout: NodeJS.Timeout | null = null
  let showSaveIndicator = $state(false)
  let fontDropdownOpen = $state(false)
  let fontSizeDropdownOpen = $state(false)

  // Font size options
  const fontSizeOptions = [
    { value: 'small', label: 'Small (14px)' },
    { value: 'medium', label: 'Medium (16px)' },
    { value: 'large', label: 'Large (18px)' }
  ]

  function getFontSizeOption(value: string) {
    return fontSizeOptions.find(opt => opt.value === value) || fontSizeOptions[1]
  }

  function selectFontSize(value: 'small' | 'medium' | 'large') {
    fontSize = value
    fontSizeDropdownOpen = false
  }

  // Font options with display names and actual font families
  const fontOptions = [
    {
      value: 'system-default',
      label: 'System Default',
      family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    {
      value: 'noto-sans-kr',
      label: 'Noto Sans KR',
      family: '"Noto Sans KR", -apple-system, sans-serif'
    },
    {
      value: 'noto-serif-kr',
      label: 'Noto Serif KR',
      family: '"Noto Serif KR", Georgia, serif'
    },
    {
      value: 'gowun-batang',
      label: 'Gowun Batang',
      family: '"Gowun Batang", serif'
    },
    {
      value: 'gowun-dodum',
      label: 'Gowun Dodum',
      family: '"Gowun Dodum", sans-serif'
    },
    {
      value: 'nanum-gothic',
      label: 'Nanum Gothic',
      family: '"Nanum Gothic", sans-serif'
    },
    {
      value: 'nanum-myeongjo',
      label: 'Nanum Myeongjo',
      family: '"Nanum Myeongjo", serif'
    },
    {
      value: 'ibm-plex-sans-kr',
      label: 'IBM Plex Sans KR',
      family: '"IBM Plex Sans KR", sans-serif'
    },
    {
      value: 'georgia',
      label: 'Georgia',
      family: 'Georgia, serif'
    },
    {
      value: 'sf-mono',
      label: 'SF Mono',
      family: '"SF Mono", ui-monospace, monospace'
    },
    {
      value: 'consolas',
      label: 'Consolas',
      family: 'Consolas, "Courier New", monospace'
    },
    {
      value: 'monaco',
      label: 'Monaco',
      family: 'Monaco, "Courier New", monospace'
    }
  ]

  function getFontOption(value: string) {
    return fontOptions.find(opt => opt.value === value) || fontOptions[0]
  }

  function selectFont(value: 'system-default' | 'noto-sans-kr' | 'noto-serif-kr' | 'gowun-batang' | 'gowun-dodum' | 'nanum-gothic' | 'nanum-myeongjo' | 'ibm-plex-sans-kr' | 'georgia' | 'sf-mono' | 'consolas' | 'monaco') {
    fontFamily = value
    fontDropdownOpen = false
  }

  // Close dropdown when clicking outside
  function handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement
    if (!target.closest('.custom-select-wrapper')) {
      fontDropdownOpen = false
      fontSizeDropdownOpen = false
    }
  }

  // Load current settings
  $effect(() => {
    const currentSettings = settingsStore.get()
    if (currentSettings.editor) {
      theme = currentSettings.editor.theme || 'auto'
      fontSize = (currentSettings.editor.fontSize as 'small' | 'medium' | 'large') || 'medium'

      // Migrate old font family values to new ones
      let loadedFontFamily = currentSettings.editor.fontFamily || 'system-default'
      if (loadedFontFamily === 'default') {
        loadedFontFamily = 'system-default'
      } else if (loadedFontFamily === 'serif') {
        loadedFontFamily = 'noto-serif-kr'
      } else if (loadedFontFamily === 'mono') {
        loadedFontFamily = 'sf-mono'
      }
      fontFamily = loadedFontFamily as 'system-default' | 'noto-sans-kr' | 'noto-serif-kr' | 'gowun-batang' | 'gowun-dodum' | 'nanum-gothic' | 'nanum-myeongjo' | 'ibm-plex-sans-kr' | 'georgia' | 'sf-mono' | 'consolas' | 'monaco'
    }
  })

  // Apply theme to document
  function applyTheme(themeValue: 'light' | 'dark' | 'auto') {
    const root = document.documentElement

    if (themeValue === 'auto') {
      root.classList.remove('light-theme', 'dark-theme')
    } else if (themeValue === 'light') {
      root.classList.remove('dark-theme')
      root.classList.add('light-theme')
    } else if (themeValue === 'dark') {
      root.classList.remove('light-theme')
      root.classList.add('dark-theme')
    }
  }

  // Apply font size using CSS variable (uses settings.ts function)
  function applyFontSize(sizeValue: 'small' | 'medium' | 'large') {
    const fontSize = fontSizeMap[sizeValue]
    updateFontSizeVariables(fontSize)
  }

  // Apply font family using CSS variable (uses settings.ts function)
  function applyFontFamily(familyValue: 'system-default' | 'noto-sans-kr' | 'noto-serif-kr' | 'gowun-batang' | 'gowun-dodum' | 'nanum-gothic' | 'nanum-myeongjo' | 'ibm-plex-sans-kr' | 'georgia' | 'sf-mono' | 'consolas' | 'monaco') {
    updateFontFamilyVariables(familyValue)
  }

  // Watch for changes and apply immediately
  $effect(() => {
    applyTheme(theme)
    applyFontSize(fontSize)
    applyFontFamily(fontFamily)

    // Skip initial load
    if (theme) {
      autoSaveSettings()
    }
  })

  // Auto-save settings with debounce
  function autoSaveSettings() {
    if (saveTimeout) clearTimeout(saveTimeout)

    showSaveIndicator = true

    saveTimeout = setTimeout(() => {
      settingsActions.updateEditor({
        theme,
        fontSize,
        fontFamily
      })

      setTimeout(() => {
        showSaveIndicator = false
      }, 1000)
    }, 500)
  }

  // Initialize on mount
  onMount(() => {
    const currentSettings = settingsStore.get()
    if (currentSettings.editor) {
      if (currentSettings.editor.theme) applyTheme(currentSettings.editor.theme)
      if (currentSettings.editor.fontSize) applyFontSize(currentSettings.editor.fontSize as 'small' | 'medium' | 'large')
      if (currentSettings.editor.fontFamily) {
        applyFontFamily(currentSettings.editor.fontFamily as 'system-default' | 'noto-sans-kr' | 'noto-serif-kr' | 'gowun-batang' | 'gowun-dodum' | 'nanum-gothic' | 'nanum-myeongjo' | 'ibm-plex-sans-kr' | 'georgia' | 'sf-mono' | 'consolas' | 'monaco')
      }
    }

    // Close font dropdown when clicking outside
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  })
</script>

<div class="appearance-settings">
  <h2 class="section-title">모양 설정</h2>
  <p class="section-description">
    애플리케이션의 전체적인 테마와 외관을 설정합니다.
  </p>
  
  <!-- Theme Settings -->
  <div class="settings-group">
    <h3 class="group-title">테마 선택</h3>
    <p class="group-description">시스템 설정을 따르거나 원하는 테마를 직접 선택할 수 있습니다.</p>
    
    <div class="theme-options">
      <label class="theme-option">
        <input 
          type="radio" 
          name="theme" 
          value="light" 
          bind:group={theme}
        />
        <div class="theme-preview light">
          <div class="preview-header">
            <div class="preview-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
          <div class="preview-content">
            <div class="preview-text"></div>
            <div class="preview-text short"></div>
          </div>
          <span class="theme-label">라이트</span>
          <span class="theme-description">밝은 테마</span>
        </div>
      </label>
      
      <label class="theme-option">
        <input 
          type="radio" 
          name="theme" 
          value="dark" 
          bind:group={theme}
        />
        <div class="theme-preview dark">
          <div class="preview-header">
            <div class="preview-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
          <div class="preview-content">
            <div class="preview-text"></div>
            <div class="preview-text short"></div>
          </div>
          <span class="theme-label">다크</span>
          <span class="theme-description">어두운 테마</span>
        </div>
      </label>
      
      <label class="theme-option">
        <input 
          type="radio" 
          name="theme" 
          value="auto" 
          bind:group={theme}
        />
        <div class="theme-preview auto">
          <div class="preview-header">
            <div class="preview-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
          <div class="preview-content">
            <div class="preview-text"></div>
            <div class="preview-text short"></div>
          </div>
          <span class="theme-label">자동</span>
          <span class="theme-description">시스템 설정 따름</span>
        </div>
      </label>
    </div>
  </div>

  <!-- Editor Settings -->
  <div class="settings-group">
    <h3 class="group-title">에디터 설정</h3>

    <div class="setting-row">
      <div class="setting-info">
        <span class="setting-label">글꼴 크기</span>
        <span class="setting-hint">에디터 본문의 기본 텍스트 크기</span>
      </div>
      <div class="custom-select-wrapper">
        <button
          class="custom-select-trigger"
          onclick={() => fontSizeDropdownOpen = !fontSizeDropdownOpen}
          type="button"
        >
          <span class="selected-font">
            {getFontSizeOption(fontSize).label}
          </span>
          <span class="select-arrow" class:open={fontSizeDropdownOpen}>
            <ChevronDown size={16} />
          </span>
        </button>
        {#if fontSizeDropdownOpen}
          <div class="custom-select-dropdown">
            {#each fontSizeOptions as option}
              <button
                class="custom-select-option"
                class:selected={fontSize === option.value}
                onclick={() => selectFontSize(option.value as 'small' | 'medium' | 'large')}
                type="button"
              >
                <span class="option-label">{option.label}</span>
                {#if fontSize === option.value}
                  <span class="option-check">✓</span>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>
    </div>

    <div class="setting-row">
      <div class="setting-info">
        <span class="setting-label">글꼴</span>
        <span class="setting-hint">에디터 본문의 글꼴 종류</span>
      </div>
      <div class="custom-select-wrapper">
        <button
          class="custom-select-trigger"
          onclick={() => fontDropdownOpen = !fontDropdownOpen}
          type="button"
        >
          <span class="selected-font" style="font-family: {getFontOption(fontFamily).family}">
            {getFontOption(fontFamily).label}
          </span>
          <span class="select-arrow" class:open={fontDropdownOpen}>
            <ChevronDown size={16} />
          </span>
        </button>
        {#if fontDropdownOpen}
          <div class="custom-select-dropdown">
            {#each fontOptions as option}
              <button
                class="custom-select-option"
                class:selected={fontFamily === option.value}
                style="font-family: {option.family}"
                onclick={() => selectFont(option.value as 'system-default' | 'noto-sans-kr' | 'noto-serif-kr' | 'gowun-batang' | 'gowun-dodum' | 'nanum-gothic' | 'nanum-myeongjo' | 'ibm-plex-sans-kr' | 'georgia' | 'sf-mono' | 'consolas' | 'monaco')}
                type="button"
              >
                <span class="option-label">{option.label}</span>
                {#if fontFamily === option.value}
                  <span class="option-check">✓</span>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
  
  <!-- Auto-save indicator -->
  {#if showSaveIndicator}
    <div class="save-indicator">
      <span class="save-icon">✓</span>
      <span>저장됨</span>
    </div>
  {/if}
</div>

<style>
  .appearance-settings {
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .section-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 24px 0;
  }
  
  .settings-group {
    padding: 24px 0;
    border-bottom: 1px solid var(--border-primary);
  }
  
  .settings-group:last-child {
    border-bottom: none;
  }
  
  .group-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .group-description {
    font-size: 12px;
    color: var(--text-secondary);
    margin: 0 0 16px 0;
  }
  
  /* Theme Options */
  .theme-options {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  
  .theme-option {
    cursor: pointer;
  }
  
  .theme-option input {
    display: none;
  }
  
  .theme-preview {
    border: 2px solid var(--border-primary);
    border-radius: 6px;
    overflow: hidden;
    transition: all 0.2s;
    position: relative;
  }
  
  .preview-header {
    padding: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
  
  .preview-dots {
    display: flex;
    gap: 4px;
  }
  
  .preview-dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: block;
  }
  
  .theme-preview.light .preview-header {
    background: #f3f4f6;
  }
  
  .theme-preview.light .preview-dots span {
    background: #d1d5db;
  }
  
  .theme-preview.light .preview-content {
    background: white;
    padding: 12px;
    height: 80px;
  }
  
  .theme-preview.light .preview-text {
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    margin-bottom: 6px;
  }
  
  .theme-preview.light .preview-text.short {
    width: 60%;
  }
  
  .theme-preview.dark .preview-header {
    background: #1f2937;
  }
  
  .theme-preview.dark .preview-dots span {
    background: #4b5563;
  }
  
  .theme-preview.dark .preview-content {
    background: #111827;
    padding: 12px;
    height: 80px;
  }
  
  .theme-preview.dark .preview-text {
    height: 8px;
    background: #374151;
    border-radius: 4px;
    margin-bottom: 6px;
  }
  
  .theme-preview.dark .preview-text.short {
    width: 60%;
  }
  
  .theme-preview.auto {
    background: linear-gradient(135deg, white 50%, #111827 50%);
  }
  
  .theme-preview.auto .preview-header {
    background: linear-gradient(135deg, #f3f4f6 50%, #1f2937 50%);
  }
  
  .theme-preview.auto .preview-dots span {
    background: #9ca3af;
  }
  
  .theme-preview.auto .preview-content {
    padding: 12px;
    height: 80px;
  }
  
  .theme-preview.auto .preview-text {
    height: 8px;
    background: linear-gradient(135deg, #e5e7eb 50%, #374151 50%);
    border-radius: 4px;
    margin-bottom: 6px;
  }
  
  .theme-preview.auto .preview-text.short {
    width: 60%;
  }
  
  .theme-label {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 12px;
    font-weight: 600;
    color: var(--text-primary);
    background: var(--bg-primary);
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid var(--border-primary);
  }
  
  .theme-description {
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: var(--text-secondary);
    white-space: nowrap;
  }
  
  .theme-option input:checked + .theme-preview {
    border-color: var(--accent-primary);
  }

  /* Editor Settings */
  .setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
  }

  .setting-row + .setting-row {
    border-top: 1px solid var(--divider);
  }

  .setting-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  .setting-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .setting-hint {
    font-size: 12px;
    color: var(--text-secondary);
  }

  /* Custom Select */
  .custom-select-wrapper {
    position: relative;
    min-width: 280px;
  }

  .custom-select-trigger {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .custom-select-trigger:hover {
    border-color: var(--border-secondary);
    background-color: var(--bg-secondary);
  }

  .selected-font {
    flex: 1;
    text-align: left;
    font-weight: 400;
  }

  .select-arrow {
    display: flex;
    align-items: center;
    color: var(--text-secondary);
    transition: transform 0.2s;
  }

  .select-arrow.open {
    transform: rotate(180deg);
  }

  .select-arrow :global(svg) {
    stroke-width: 2;
  }

  .custom-select-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
    overflow: hidden;
    animation: slideDown 0.15s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .custom-select-option {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: background 0.15s;
    text-align: left;
  }

  .custom-select-option:hover {
    background: var(--bg-secondary);
  }

  .custom-select-option.selected {
    background: var(--bg-tertiary);
  }

  .custom-select-option + .custom-select-option {
    border-top: 1px solid var(--divider);
  }

  .option-label {
    flex: 1;
    font-size: 13px;
    font-weight: 400;
    color: var(--text-primary);
    text-align: left;
  }

  .option-check {
    font-size: 14px;
    color: var(--accent-primary);
    font-weight: bold;
  }
  
  /* Save Indicator */
  .save-indicator {
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--success);
    color: white;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease;
    z-index: 1000;
  }
  
  .save-icon {
    font-size: 14px;
    font-weight: bold;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Font size variations */
  :global(.font-small) {
    font-size: 14px;
  }
  
  :global(.font-medium) {
    font-size: 16px;
  }
  
  :global(.font-large) {
    font-size: 18px;
  }
  
  /* Font family variations - apply to both container and descendants */
  :global(.font-system-default),
  :global(.font-system-default .ProseMirror),
  :global(.font-system-default textarea) {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
    font-weight: inherit;
  }

  :global(.font-noto-sans-kr),
  :global(.font-noto-sans-kr .ProseMirror),
  :global(.font-noto-sans-kr textarea) {
    font-family: 'Noto Sans KR', -apple-system, sans-serif !important;
    font-weight: inherit;
  }

  :global(.font-noto-serif-kr),
  :global(.font-noto-serif-kr .ProseMirror),
  :global(.font-noto-serif-kr textarea) {
    font-family: 'Noto Serif KR', Georgia, serif !important;
    font-weight: inherit;
  }

  :global(.font-gowun-batang),
  :global(.font-gowun-batang .ProseMirror),
  :global(.font-gowun-batang textarea) {
    font-family: 'Gowun Batang', serif !important;
    font-weight: inherit;
  }

  :global(.font-gowun-dodum),
  :global(.font-gowun-dodum .ProseMirror),
  :global(.font-gowun-dodum textarea) {
    font-family: 'Gowun Dodum', sans-serif !important;
    font-weight: inherit;
  }

  :global(.font-nanum-gothic),
  :global(.font-nanum-gothic .ProseMirror),
  :global(.font-nanum-gothic textarea) {
    font-family: 'Nanum Gothic', sans-serif !important;
    font-weight: inherit;
  }

  :global(.font-nanum-myeongjo),
  :global(.font-nanum-myeongjo .ProseMirror),
  :global(.font-nanum-myeongjo textarea) {
    font-family: 'Nanum Myeongjo', serif !important;
    font-weight: inherit;
  }

  :global(.font-ibm-plex-sans-kr),
  :global(.font-ibm-plex-sans-kr .ProseMirror),
  :global(.font-ibm-plex-sans-kr textarea) {
    font-family: 'IBM Plex Sans KR', sans-serif !important;
    font-weight: inherit;
  }

  :global(.font-georgia),
  :global(.font-georgia .ProseMirror),
  :global(.font-georgia textarea) {
    font-family: Georgia, serif !important;
    font-weight: inherit;
  }

  :global(.font-sf-mono),
  :global(.font-sf-mono .ProseMirror),
  :global(.font-sf-mono textarea) {
    font-family: 'SF Mono', ui-monospace, monospace !important;
    font-weight: inherit;
  }

  :global(.font-consolas),
  :global(.font-consolas .ProseMirror),
  :global(.font-consolas textarea) {
    font-family: Consolas, 'Courier New', monospace !important;
    font-weight: inherit;
  }

  :global(.font-monaco),
  :global(.font-monaco .ProseMirror),
  :global(.font-monaco textarea) {
    font-family: Monaco, 'Courier New', monospace !important;
    font-weight: inherit;
  }
</style>